import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';
import { SessionService } from './session.service';
import { UserService } from './user/user.service';
import { FeatureFlagsService } from './featureFlags.service';

@Injectable({
  providedIn: 'root',
})
export class PosthogService {
  private initialized = false;
  private enabled = false;

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private userService: UserService,
    private featureFlagsService: FeatureFlagsService
  ) {
    this.initPostHog();
    this.trackNavigation();
    this.watchFacilityChanges();
  }

  /**
   * Set facility context - will be empty for sellers ad admins
   */
  public setFacilityContext(facility: any): void {
    if (!this.enabled || !facility) return;

    // Update persistent properties
    const props: any = {}
    props.facility_id = facility.id;
    props.facility_name = facility.name;
    this.registerSuperProperties(props);
    this.setUserProperties(props);
  }

  /**
   * Set company context for session
   */
  public setCompanyContext(company: any): void {
    if (!this.enabled || !company) return;

    console.log('setCompanyContext', company);
    // Update persistent properties
    const props: any = {}
    props.company_id = company.id;
    props.company_name = company.name;
    console.log('setCompanyContext props', props);
    this.registerSuperProperties(props);
    this.setUserProperties(props);
  }

  public IdentifyPosthogUser() {

    this.userService.getMe().subscribe(res => {
      // Set context super properties immediately
      this.setFacilityContext(res.data.primary_facility);
      this.setCompanyContext(res.data.company);

      // Construct distinct_id to match server-side format: namespace_env_companyId_userId
      const envName = (environment as any).env_name;
      const clientNamespace = (environment as any).supplierConfig?.clientNameSpace;
      const distinctId = `${clientNamespace}_${envName}_${res.data.company?.id}_${res.data.id}`;

      const userProperties = {
        email: res.data.email,
        name: res.data.name || res.data.email,
        role: res.data.role, // Primary Role
        roles: res.data.roles, // All Roles
        facility_id: res.data.primary_facility?.id,
        facility_name: res.data.primary_facility?.name,
        facility_type: res.data.primary_facility?.type,
        company_id: res.data.company?.id,
        company_name: res.data.company?.name
      };

      // Register roles as a super property for easy filtering
      this.registerSuperProperties({ roles: res.data.roles });

      // Identify user in PostHog
      this.identify(distinctId, userProperties);

    }, err => console.warn('PostHog: Failed to resolve facility/company name', err));
  }

  /**
   * Set group context for the session
   */
  public setGroup(groupType: string, groupKey: string, properties?: Record<string, any>): void {
    if (!this.enabled || !groupKey) return;
    posthog.group(groupType, groupKey, properties);
  }

  /**
   * Watch for facility changes and update global context
   */
  private watchFacilityChanges(): void {
    this.sessionService.primaryFacilityUpdatedTrigger.subscribe(() => {
      // Fetch latest profile to get the confirmed active facility
      this.userService.getMe().subscribe(
        res => {
          const facility = res.data?.primary_facility;
          const company = res.data?.company;

          if (facility) {
            this.setFacilityContext(facility);
          } else {
            console.warn('PostHog: No primary facility found in profile');
          }
        },
        err => console.warn('PostHog: Failed to update facility context', err),
      );
    });
  }

  /**
   * Listen to Angular Router events to track pageviews
   */
  private trackNavigation(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      if (this.initialized) {
        posthog.capture('$pageview');
      }
    });
  }

  /**
   * Initialize PostHog with autocapture
   */
  private initPostHog(): void {
    // Check if posthog config exists and is enabled
    if (!(environment as any).posthog || !(environment as any).posthog.enabled) {
      return;
    }

    this.enabled = true;

    if (this.initialized) {
       return;
    }

    const posthogConfig = (environment as any).posthog;

    try {
      posthog.init(posthogConfig.apiKey, {
        api_host: posthogConfig.apiHost,
        
        // Autocapture configuration - restrict to submits only to reduce noise
        autocapture: {
          dom_event_allowlist: ['submit']
          // dom_event_allowlist: ['click', 'submit']
        },
        // Disable default capture_pageview since we are manual tracking with router
        capture_pageview: false, 
        capture_pageleave: true,
        
        // Session recording
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: posthogConfig.session_recording?.maskAllInputs ?? true,
          maskTextSelector: posthogConfig.session_recording?.maskTextSelector,
          recordCrossOriginIframes: false,
        },
        
        // Privacy and compliance
        mask_all_text: false,
        mask_all_element_attributes: false,
        
        // Default to opted-out until whitelist check passes
        opt_out_capturing_by_default: true,

        // Never capture these properties (HIPAA compliance)
        property_blacklist: [
          'password',
          'ssn',
          'social_security',
          'credit_card',
          'cvv',
          'patient_name',
          'medical_record',
          'diagnosis',
          'dob',
          'date_of_birth',
        ],
        
        // Performance
        loaded: (posthog) => {
          this.initialized = true;
          
          // Register global groups with properties
          const envName = (environment as any).env_name;
          const namespace = (environment as any).supplierConfig?.clientNameSpace;
          const hostname = window.location.hostname;

          this.setGroup('env', envName, { name: envName, type: 'environment' });
          this.setGroup('namespace', namespace, { name: namespace, type: 'k8s_namespace' });
          this.setGroup('source_domain', hostname, { name: hostname, type: 'domain' });

          // Check whitelist for initial capture logic
          const envNamespace = (environment as any).supplierConfig?.clientNameSpace;
          this.evaluateTracking(null, envNamespace);

          // Opt out for internal/admin users
          this.checkOptOut();
        },
      });
    } catch (error) {
      console.error('Failed to initialize PostHog:', error);
    }
  }

  /**
   * Opt out internal users from tracking
   */
  private checkOptOut(): void {
    // Don't track on admin routes
    if (window.location.href.includes('/admin')) {
      posthog.opt_out_capturing();
      console.log('PostHog: Opted out for admin users');
    }
  }

  /**
   * Evaluate (and toggle) tracking based on whitelist
   */
  private evaluateTracking(email: string | null, namespace: string): void {
    const payload = this.featureFlagsService.get('posthog_whitelist');

    let captureEnabled = false;
    let recordingEnabled = false;
    let debug = false;

    if (payload) {
      const config = typeof payload === 'string' ? JSON.parse(payload) : payload;
      debug = config.debug || false;

      if (debug) {
        console.log('PostHog Debug: Raw Feature Flag Payload', payload);
        console.log('PostHog Debug: Parsed Config', config);
        console.log('PostHog Debug: Current Context', { email, namespace });
      }

      // Session Recording Lists (Full Access - implies Capture)
      // Check for nested 'session_recording' object or legacy root keys
      const recNamespaces = config.session_recording?.client_namespaces || config.client_namespaces || [];
      const recEmails = config.session_recording?.user_emails || config.user_emails || [];

      // Event Capture Lists (Events Only)
      const capNamespaces = config.event_capture?.client_namespaces || [];

      // Check Recording Whitelist
      if ((namespace && recNamespaces.includes(namespace)) || (email && recEmails.includes(email))) {
        recordingEnabled = true;
        captureEnabled = true; // Recording requires capture
      }

      // Check Capture Whitelist (if not already enabled)
      if (!captureEnabled) {
        if (namespace && capNamespaces.includes(namespace)) {
          captureEnabled = true;
        }
      }
    }

    // Apply Capture State
    if (captureEnabled) {
      if (debug) console.log('PostHog: Event Capture ENABLED via whitelist');
      posthog.opt_in_capturing();
    } else {
      if (debug) console.log('PostHog: Event Capture DISABLED');
      posthog.opt_out_capturing();
    }

    // Apply Recording State
    if (recordingEnabled) {
      if (debug) console.log('PostHog: Session Recording ENABLED via whitelist');
      posthog.startSessionRecording();
    } else {
      if (debug) console.log('PostHog: Session Recording DISABLED');
      posthog.stopSessionRecording();
    }
  }

  /**
   * Identify a user (call on login)
   */
  identify(userId: string, properties?: Record<string, any>): void {
    if (!this.enabled) return;
    
    posthog.identify(userId, properties);

    // Check whitelist for session recording and tracking
    const email = properties?.email;
    const namespace = properties?.client_namespace || (environment as any).supplierConfig?.clientNameSpace;
    this.evaluateTracking(email, namespace);
  }

  /**
   * Reset user identity (call on logout)
   */
  reset(): void {
    if (!this.initialized) return;
    
    posthog.reset();
  }

  /**
   * Capture a custom event
   * Use this for events that can't be autocaptured (computed values, backend events, etc.)
   */
  capture(eventName: string, properties?: Record<string, any>): void {
    if (!this.enabled) return;
    posthog.capture(eventName, properties);
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.enabled) return;
    
    posthog.setPersonProperties(properties);
  }

  /**
   * Register super properties to be included with all events
   */
  registerSuperProperties(properties: Record<string, any>): void {
    if (!this.enabled) return;

    posthog.register(properties);
  }

  /**
   * Check if a feature flag is enabled
   */
  isFeatureEnabled(flagKey: string): boolean {
    if (!this.initialized) return false;
    
    return posthog.isFeatureEnabled(flagKey) ?? false;
  }

  /**
   * Get feature flag value
   */
  getFeatureFlag(flagKey: string): string | boolean | undefined {
    if (!this.initialized) return undefined;
    
    return posthog.getFeatureFlag(flagKey);
  }

  /**
   * Start session recording (useful for high-value users)
   */
  startSessionRecording(): void {
    if (!this.initialized) return;
    
    posthog.startSessionRecording();
  }

  /**
   * Stop session recording
   */
  stopSessionRecording(): void {
    if (!this.initialized) return;
    
    posthog.stopSessionRecording();
  }

  /**
   * Opt user out of tracking
   */
  optOut(): void {
    if (!this.initialized) return;
    
    posthog.opt_out_capturing();
  }

  /**
   * Opt user back in to tracking
   */
  optIn(): void {
    if (!this.initialized) return;
    
    posthog.opt_in_capturing();
  }
}
